import type { ReactNode } from "react";
import { buttonVariants } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * A **deferred** action affordance (Phase 11 / FS Phase 6): a disabled-looking button whose "coming soon" reason must still
 * reach keyboard + screen-reader users. A native `disabled` button drops out of the tab order — so its {@link Tooltip} would
 * be hover-only. Instead this is a **single focusable, `aria-disabled` element** styled AS a button (`buttonVariants`) that is
 * itself the tooltip trigger — react-aria surfaces the tooltip on focus AND hover. It carries no press handler, so it's inert
 * by construction (no nested real `<button>`, so nothing double-announces).
 *
 * Lives in shared UI (not a feature folder) because both the manual-editor (StepMedia) and the asset-library drawer use it;
 * keeping it here breaks the asset-library → manual-editor import cycle (the picker must not depend back on its consumer).
 */
export function DeferredActionButton({
  label,
  tooltip,
  icon,
  variant = "default",
  className,
}: {
  label: string;
  tooltip: string;
  icon: ReactNode;
  variant?: "default" | "outline";
  className?: string;
}) {
  return (
    <TooltipTrigger delay={0}>
      <span
        role="button"
        aria-disabled="true"
        tabIndex={0}
        className={cn(buttonVariants({ variant, size: "sm" }), "cursor-not-allowed gap-1.5 opacity-50 active:translate-y-0", className)}
      >
        {icon}
        {label}
      </span>
      <Tooltip>{tooltip}</Tooltip>
    </TooltipTrigger>
  );
}
